import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { MemoryRouter, Route, Routes } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { ErrorNotice } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { Editor, type EditorEmbedding } from '@/proposals/editor/Editor';
import { getProposalPdf } from '@/proposals/lib/api';
import { FEATURES } from '@/lib/flags';
import { AssistantSurfaceContext, type AssistantSurface } from '@/components/assistant/surface';
import { saveBlob } from '@/lib/download';
import { NewTabRouter } from './NewTabRouter';
import {
  EDITOR_MESSAGE,
  REFRESH_RETRY_MS,
  parentOrigin,
  refreshDelayMs,
  refreshEditorToken,
  type EditorMessageType,
  type EditorSession,
} from './session';

/** What the Salesforce window receives. */
export interface EditorMessage {
  readonly type: EditorMessageType;
  readonly proposalId: string;
}

/** Posts one message to the Salesforce window. */
export type PostToParent = (message: EditorMessage) => void;

/** The window pieces {@link parentPoster} reads (a test passes its own). */
export interface FrameWindow {
  readonly document: { readonly referrer: string };
  readonly location: { readonly ancestorOrigins?: ArrayLike<string> };
  readonly parent: { postMessage(message: unknown, targetOrigin: string): void };
}

/**
 * A poster bound to the Salesforce window's exact origin, or null when the
 * parent is not one the frame-ancestors list allows — then nothing is ever
 * posted (never to `*`).
 */
export function parentPoster(win: FrameWindow, ancestors: readonly string[]): PostToParent | null {
  const origin = parentOrigin(win.document.referrer, ancestors, Array.from(win.location.ancestorOrigins ?? []));
  if (!origin) return null;
  return (message) => win.parent.postMessage(message, origin);
}

/** Loaded on first open, as in the app shell (it carries the markdown renderer). */
const AssistantPanel = lazy(() =>
  import('@/components/assistant/AssistantPanel').then((module) => ({ default: module.AssistantPanel })),
);

/** The token writes proposals only, so draft cards for other records hand off to BCE Cloud. */
const EMBED_SURFACE: AssistantSurface = { savesRecords: false };

const SESSION_ENDED_TITLE = 'This editing session ended';
const SESSION_ENDED_MESSAGE = 'Close this window and click Edit again.';
const UNAUTHORIZED = 401;

function isSessionEnded(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === UNAUTHORIZED;
}

function FullPageNotice({ title, message }: { readonly title: string; readonly message: string }): React.ReactElement {
  return (
    <main className="grid min-h-screen place-items-center bg-portal-bg p-6">
      <div className="w-full max-w-[440px]">
        <ErrorNotice title={title} message={message} />
      </div>
    </main>
  );
}

const STATUS = { active: 'active', ended: 'ended', failed: 'failed' } as const;
type Status =
  | { readonly kind: typeof STATUS.active }
  | { readonly kind: typeof STATUS.ended }
  | { readonly kind: typeof STATUS.failed; readonly message: string };

export interface EmbeddedEditorAppProps {
  /** Null when the URL carried no usable token. */
  readonly session: EditorSession | null;
  /** Null when the parent is not an allowed Salesforce origin. */
  readonly postToParent: PostToParent | null;
  readonly now?: () => Date;
}

/**
 * `/embed/proposals/:id` — the staff proposal editor inside Salesforce's
 * full-screen modal. No Clerk gate and no app shell (like `/print/:id`): the
 * scoped Editor token is the only credential. No navigation or delete; a Done
 * button that saves and tells Salesforce to close; a "saved" message after
 * every save so Salesforce can refresh its list. The assistant opens beside
 * the editor from the top bar, and any link it offers opens BCE Cloud in a
 * new tab so this frame stays on the proposal.
 *
 * @param props - The session and the parent poster.
 * @returns The embedded editor, or the full-page "session ended" notice.
 */
export function EmbeddedEditorApp({ session, postToParent, now = () => new Date() }: EmbeddedEditorAppProps): React.ReactElement {
  const [status, setStatus] = useState<Status>(session ? { kind: STATUS.active } : { kind: STATUS.ended });
  const [assistantOpen, setAssistantOpen] = useState(false);
  const toggleAssistant = useCallback(() => setAssistantOpen((open) => !open), []);

  // Keep the token alive: refresh ~5 minutes before it lapses; a 401 means the
  // session is over (8 hours, user deactivated, permission removed).
  useEffect(() => {
    if (!session || status.kind !== STATUS.active) return undefined;
    let timer = 0;
    let cancelled = false;
    const schedule = (delayMs: number): void => {
      timer = window.setTimeout(() => {
        refreshEditorToken(session.proposalId)
          .then((next) => {
            if (cancelled) return;
            session.replace(next);
            schedule(refreshDelayMs(next.expiresAt, now()));
          })
          .catch((error: unknown) => {
            if (cancelled) return;
            const lapsed = session.current().expiresAt.getTime() <= now().getTime();
            if (isSessionEnded(error) || lapsed) setStatus({ kind: STATUS.ended });
            else schedule(REFRESH_RETRY_MS);
          });
      }, delayMs);
    };
    schedule(refreshDelayMs(session.current().expiresAt, now()));
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `now` is a clock, not state; the loop restarts only for a new session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, status.kind]);

  const proposalId = session?.proposalId ?? '';
  const post = useCallback(
    (type: EditorMessageType) => postToParent?.({ type, proposalId }),
    [postToParent, proposalId],
  );

  const embedding = useMemo<EditorEmbedding>(
    () => ({
      onSaved: () => post(EDITOR_MESSAGE.saved),
      onDone: () => post(EDITOR_MESSAGE.done),
      onLoadError: (error) =>
        setStatus(isSessionEnded(error) ? { kind: STATUS.ended } : { kind: STATUS.failed, message: errorMessage(error) }),
      onExportPdf: () => {
        const pending = toast.loading('Preparing the PDF…');
        getProposalPdf(proposalId)
          .then((pdf) => {
            saveBlob(pdf.blob, pdf.fileName);
            toast.success('PDF downloaded', { id: pending });
          })
          .catch((error: unknown) => {
            if (isSessionEnded(error)) {
              toast.dismiss(pending);
              setStatus({ kind: STATUS.ended });
              return;
            }
            toast.error(errorMessage(error), { id: pending });
          });
      },
      ...(FEATURES.assistant ? { assistant: { open: assistantOpen, onToggle: toggleAssistant } } : {}),
    }),
    [post, proposalId, assistantOpen, toggleAssistant],
  );

  if (status.kind === STATUS.ended || !session) {
    return <FullPageNotice title={SESSION_ENDED_TITLE} message={SESSION_ENDED_MESSAGE} />;
  }
  if (status.kind === STATUS.failed) {
    return <FullPageNotice title="This proposal could not be opened" message={status.message} />;
  }
  const editorPath = `/proposals/${session.proposalId}`;
  return (
    <div className="flex h-screen min-h-0 min-w-0 bg-background">
      <MemoryRouter initialEntries={[editorPath]}>
        <div className="flex min-h-0 min-w-0 flex-1">
          <Routes>
            <Route path="/proposals/:id" element={<Editor embedding={embedding} />} />
          </Routes>
        </div>
      </MemoryRouter>
      {FEATURES.assistant && assistantOpen ? (
        // Its own router: the panel reads this proposal as its screen, and
        // its links open BCE Cloud in a new tab instead of moving the editor.
        <NewTabRouter path={editorPath}>
          <AssistantSurfaceContext.Provider value={EMBED_SURFACE}>
            <Suspense fallback={null}>
              <AssistantPanel onClose={() => setAssistantOpen(false)} />
            </Suspense>
          </AssistantSurfaceContext.Provider>
        </NewTabRouter>
      ) : null}
      <Toaster position="bottom-right" closeButton />
    </div>
  );
}
